import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Chip
} from '@mui/material';
import { API_BASE_URL } from '../config';
import { authenticatedFetch, apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import ItineraryModal from './ItineraryModal';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';

/**
 * External Itinerary - Standalone interface for AIIMS integration
 * 
 * Features:
 * - Accepts JWT token via URL parameter
 * - Validates token with backend
 * - Shows guest itinerary using ItineraryModal (same as DoctorDesk)
 * - Guest pre-selected and locked
 * - Session timer with warning
 * - Auto-logout on expiry
 * - Full PDF download capability
 * - Activities table
 * - Wellness guidelines
 */
const ExternalItinerary = () => {
  // Authentication state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [showWarning, setShowWarning] = useState(false);

  // Extract token from URL
  const getTokenFromURL = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('token');
  }, []);

  // Authenticate with backend using AIIMS JWT
  const authenticateWithToken = useCallback(async (token) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiPost('/api/auth/external-aiims-login', { token });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Authentication failed');
      }

      const data = await response.json();
      
      // Store access token in sessionStorage (not localStorage) to prevent conflicts
      // with regular login sessions. External session is tab-specific.
      sessionStorage.setItem('externalAccessToken', data.access_token);
      sessionStorage.setItem('externalSession', JSON.stringify({
        guest_id: data.guest_id,
        guest_name: data.guest_name,
        doctor_context: data.doctor_context,
        expires_at: data.expires_at
      }));

      setSessionData({
        guestId: data.guest_id,
        guestName: data.guest_name,
        doctorContext: data.doctor_context,
        expiresAt: new Date(data.expires_at)
      });

      setAuthenticated(true);
      setLoading(false);
    } catch (err) {
      console.error('Authentication error:', err);
      setError(err.message || 'Failed to authenticate. Please try again.');
      setLoading(false);
    }
  }, []);

  // Calculate time remaining
  const updateTimeRemaining = useCallback(() => {
    if (!sessionData?.expiresAt) return;

    const now = new Date();
    const expiry = sessionData.expiresAt;
    const diffMs = expiry - now;
    const diffMinutes = Math.floor(diffMs / 60000);

    setTimeRemaining(diffMinutes);

    if (diffMinutes <= 5 && diffMinutes > 0) {
      setShowWarning(true);
    }

    if (diffMinutes <= 0) {
      handleLogout();
    }
  }, [sessionData]);

  // Initial authentication
  useEffect(() => {
    const token = getTokenFromURL();
    
    if (!token) {
      setError('No authentication token provided. Please access this page via AIIMS portal.');
      setLoading(false);
      return;
    }

    authenticateWithToken(token);
  }, [getTokenFromURL, authenticateWithToken]);

  // Update time remaining every minute
  useEffect(() => {
    if (!authenticated || !sessionData) return;

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 60000);

    return () => clearInterval(interval);
  }, [authenticated, sessionData, updateTimeRemaining]);

  // Handle logout - only clear sessionStorage, preserve regular login in localStorage
  const handleLogout = () => {
    sessionStorage.removeItem('externalAccessToken');
    sessionStorage.removeItem('externalSession');
    window.location.href = '/';
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: '#f6f6f6',
          gap: 2
        }}
      >
        <CircularProgress size={60} sx={{ color: '#E4C984' }} />
        <Typography variant="h6" sx={{ color: '#666' }}>
          Authenticating...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: '#f6f6f6',
          p: 3
        }}
      >
        <Paper
          sx={{
            p: 4,
            maxWidth: 600,
            textAlign: 'center',
            border: '2px solid #f44336'
          }}
        >
          <Typography variant="h5" sx={{ color: '#f44336', mb: 2, fontWeight: 600 }}>
            Authentication Failed
          </Typography>
          <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
            {error}
          </Alert>
          <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
            Please contact your system administrator if this problem persists.
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.href = '/'}
            sx={{
              bgcolor: '#E4C984',
              color: '#171717',
              '&:hover': { bgcolor: '#d4b874' }
            }}
          >
            Return to Login
          </Button>
        </Paper>
      </Box>
    );
  }

  // Authenticated - show Itinerary
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#f6f6f6' }}>
      {/* Header */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderRadius: 0,
          bgcolor: '#fff',
          borderBottom: '3px solid #E4C984'
        }}
      >
        {/* Left: Logo and Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            component="img"
            src="/atmantan-logo-a.png"
            alt="Atmantan"
            sx={{ width: 50, height: 'auto' }}
          />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#171717' }}>
              Guest Itinerary
            </Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>
              AIIMS Integration
            </Typography>
          </Box>
        </Box>

        {/* Center: Doctor and Guest Info */}
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          {/* Doctor Info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon sx={{ color: '#E4C984', fontSize: 20 }} />
            <Box>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', lineHeight: 1 }}>
                Doctor
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {sessionData?.doctorContext?.doctor_name || 'Unknown'}
              </Typography>
            </Box>
          </Box>

          {/* Guest Info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BadgeIcon sx={{ color: '#E4C984', fontSize: 20 }} />
            <Box>
              <Typography variant="caption" sx={{ color: '#666', display: 'block', lineHeight: 1 }}>
                Guest
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {sessionData?.guestName || sessionData?.guestId || 'Unknown'}
                {sessionData?.guestName && sessionData?.guestId && (
                  <span style={{ color: '#666', fontWeight: 400, marginLeft: 4 }}>
                    ({sessionData.guestId})
                  </span>
                )}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Right: Session Timer and Logout */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip
            icon={<AccessTimeIcon />}
            label={`${timeRemaining} min remaining`}
            color={showWarning ? 'warning' : 'default'}
            sx={{
              fontWeight: 600,
              ...(showWarning && {
                bgcolor: '#ff9800',
                color: '#fff',
                '& .MuiChip-icon': { color: '#fff' }
              })
            }}
          />
          <Button
            variant="outlined"
            startIcon={<ExitToAppIcon />}
            onClick={handleLogout}
            sx={{
              borderColor: '#E4C984',
              color: '#171717',
              '&:hover': {
                borderColor: '#cbb16f',
                bgcolor: 'rgba(228, 201, 132, 0.08)'
              }
            }}
          >
            Logout
          </Button>
        </Box>
      </Paper>

      {/* Session Warning */}
      {showWarning && (
        <Alert
          severity="warning"
          sx={{ borderRadius: 0, justifyContent: 'center' }}
        >
          Your session will expire in {timeRemaining} minute{timeRemaining !== 1 ? 's' : ''}.
          You will be automatically logged out.
        </Alert>
      )}

      {/* Itinerary Modal - Rendered as full-page content */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <ItineraryModal
          open={true}
          onClose={() => {}} // No-op since we're using it as embedded content
          guestId={sessionData?.guestId}
          guestName={sessionData?.guestName}
          embedded={true} // Signal to render without dialog wrapper
        />
      </Box>
    </Box>
  );
};

export default ExternalItinerary;
